/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAuth0 } from "@auth0/auth0-react";

import { GenericTable } from "@ui-components/Table/GenericTable";
import { logsColumns, LogItem } from "./LogsData";
import { FilterByAttribute, LogRowDetails } from "./LogRowDetails";
import { getCTXRlistGlobal, getLogsByCtxrId } from "./request/request";
import GenericSearchbar from "@ui-components/Searchbar/GenericSearchbar";
import GenericDropdown from "@ui-components/Dropdown/GenericDropdown";
import LogsTimeRangePicker, {
  PresetKey,
  resolvePreset,
  TimeRange,
  TimeRangeMode,
} from "ctxReflectorV2/components/Logs/LogsTimeRangePicker";
import { getTableBodyMaxHeight } from "ctxReflectorV2/helpers/tableViewport";
import {
  buildPredicate,
  FilterField,
  labelId,
  parseToken,
  ParsedFilter,
} from "kontrolEdge/helpers/advancedFilters";

import { useAppSelector } from "@redux/hooks";
import GenericCard from "@ui-components/Card/GenericCard";
import GenericSwitch from "@ui-components/Switch/GenericSwitch";
import { makeLogFields } from "./logFieldSpec";
import { formatLogTimestamp } from "./logTimestamp";
import { SkeletonTableRows } from "@ui-components/Skeleton/GenericSkeleton";

// Same picker as Reflector V2 Logs (CTX-1337); V1 opens on the last 24 hours,
// which is what the old dashboard's range list led with.
const DEFAULT_RANGE_PRESET: PresetKey = "24h";

/** SideNav + L3 Overview tabs + filter card + table header, footer and padding.
 * Without it GenericTable caps the body at 400px, so 20 or 50 rows scroll
 * inside a box that stops halfway down the page. */
const LOGS_CHROME_PX = 400;

interface CtxrItem {
  id: string;
  name: string;
  description: string;
  subscriptionId: number;
  Suscription?: string;
}

export function LogsPage() {
  const { isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();
  const { tenantId } = useAppSelector((state) => state.selection);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const [isFetching, setIsFetching] = useState(false);
  const [tablesData, setTablesData] = useState<LogItem[]>([]);

  // CTXR
  const [fullCTXRlist, setFullCTXRlist] = useState<CtxrItem[]>([]);
  const [firstOptions, setFirstOptions] = useState<string[]>([]);
  const [selectedFirst, setSelectedFirst] = useState("");

  // Search Log's chips live here so an expanded row's filter buttons can add
  // one (CTX-1356); the bar still reports them through onFiltersChange.
  const [searchLogChips, setSearchLogChips] = useState<ParsedFilter[]>([]);

  // Filtro local con GenericSearchbar
  const [activeFilters, setActiveFilters] = useState<ParsedFilter[]>([]);
  // Each attribute parsed out of the loaded rows is a field, so it is
  // suggested and its chip filters.
  const logFields = useMemo(
    () => makeLogFields(selectedFirst, tablesData),
    [selectedFirst, tablesData]
  );
  const [simpleSearchValue, setSimpleSearchValue] = useState("");

  // Generate suggested keys from field labels
  const suggestedKeys = useMemo(() => {
    return logFields.map((f) => f.label);
  }, [logFields]);

  // Wrap messages: whole message vs one truncated line (CTX-1354)
  const [wrapMessages, setWrapMessages] = useState(true);

  // Severity
  const severityOptions = ["All Levels", "ERROR", "DEBUG", "INFO", "WARNING"];
  const [selectedSeverity, setSelectedSeverity] = useState("All Levels");

  // Rango
  const [range, setRange] = useState<TimeRange>(() =>
    resolvePreset(DEFAULT_RANGE_PRESET)
  );
  const [rangeMode, setRangeMode] = useState<TimeRangeMode>({
    kind: "preset",
    key: DEFAULT_RANGE_PRESET,
  });

  // Helper function to find CTX-R object by display name
  const findCtxrByDisplayName = useCallback(
    (displayName: string): CtxrItem | undefined => {
      return fullCTXRlist.find(
        (obj) => `${obj.name} (${obj.description})` === displayName
      );
    },
    [fullCTXRlist]
  );

  // Helper function to map API response to LogItem
  const mapLogsDataToTable = useCallback((data: any[]): LogItem[] => {
    return data.map((item) => ({
      timestamp: formatLogTimestamp(item.timestamp || ""),
      rawTimestamp: item.timestamp || "",
      level: item.level || "",
      message: item.log || "",
    }));
  }, []);

  // Centralized function to fetch logs data
  const fetchLogsData = useCallback(
    async (skipLoadingState = false) => {
      const { start, end } = range;

      const foundObj = findCtxrByDisplayName(selectedFirst);
      if (!foundObj) {
        console.warn("No CTXR found for", selectedFirst);
        return;
      }

      try {
        if (!skipLoadingState) {
          setIsFetching(true);
        }

        const token = await getAccessTokenSilently();

        const realLevel =
          selectedSeverity === "All Levels" ? undefined : selectedSeverity;
        const trimmedSearch = simpleSearchValue.trim();
        const realSearch = trimmedSearch === "" ? undefined : trimmedSearch;

        const resp = await getLogsByCtxrId(
          token,
          foundObj.id,
          tenantId,
          { start, end },
          realLevel,
          realSearch
        );

        const rawData = resp.data?.hits;
        if (!Array.isArray(rawData) || rawData.length === 0) {
          setTablesData([]);
          return;
        }

        const shaped = mapLogsDataToTable(rawData);
        setTablesData(shaped);
      } catch (err) {
        console.error("Error fetching logs =>", err);
      } finally {
        if (!skipLoadingState) {
          setIsFetching(false);
        }
      }
    },
    [
      range,
      selectedFirst,
      selectedSeverity,
      simpleSearchValue,
      tenantId,
      getAccessTokenSilently,
      findCtxrByDisplayName,
      mapLogsDataToTable,
    ]
  );

  useEffect(() => {
    if (isLoading || !isAuthenticated) return;

    // If tenantId can be 0 for global, don't use !tenantId
    if (tenantId === null || tenantId === undefined) return;

    let cancelled = false;

    const loadCtxrList = async () => {
      setIsFetching(true);
      try {
        const token = await getAccessTokenSilently();
        const resp = await getCTXRlistGlobal(-1, tenantId, token);
        const dataArray: CtxrItem[] = resp.data || [];

        if (cancelled) return;

        // Reset data when tenant changes / first loads
        setTablesData([]);
        setFullCTXRlist(dataArray);

        const mapped = dataArray.map(
          (item) => `${item.name} (${item.description})`
        );
        setFirstOptions(mapped);

        // Auto-select first CTX-R if available, otherwise clear selection
        if (mapped.length > 0) {
          setSelectedFirst(mapped[0]);
        } else {
          setSelectedFirst("");
        }

      } catch (err) {
        console.error("Error loading CTX-R list =>", err);
      } finally {
        if (!cancelled) {
          setIsFetching(false);
        }
      }
    };

    loadCtxrList();

    return () => {
      cancelled = true;
    };
  }, [isLoading, isAuthenticated, tenantId, getAccessTokenSilently]);

  const handleSelectFirst = useCallback((val: string) => {
    setSelectedFirst(val);
  }, []);

  const handleRangeChange = (next: TimeRange, mode: TimeRangeMode) => {
    setRange(next);
    setRangeMode(mode);
  };

  // A preset is re-resolved against "now" so "Last 1 hour" stays the last hour;
  // the new range re-triggers the fetch effect. A custom range is re-fetched as is.
  const refreshRange = () => {
    if (rangeMode.kind === "preset") {
      setRange(resolvePreset(rangeMode.key));
    } else {
      fetchLogsData(false);
    }
  };

  // Auto-refresh when filters change (with debouncing)
  useEffect(() => {
    // Basic guards
    if (isLoading || !isAuthenticated) return;

    // If tenantId can be 0 for global, do NOT use !tenantId
    if (tenantId === null || tenantId === undefined) return;

    if (!selectedFirst || !fullCTXRlist.length) return;

    // Clear previous debounce
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Debounce the request
    debounceTimerRef.current = setTimeout(() => {
      fetchLogsData(false);
    }, 400);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [
    tenantId,
    selectedFirst,
    selectedSeverity,
    simpleSearchValue,
    range,
    fullCTXRlist.length,
    isLoading,
    isAuthenticated,
    fetchLogsData,
  ]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Build maps for filter normalization (similar to MainPort)
  const fieldById = useMemo(() => {
    const m = new Map<string, FilterField<LogItem>>();
    for (const f of logFields) {
      const id = (f.key ?? labelId(f.label)).toLowerCase();
      m.set(id, f);
    }
    return m;
  }, [logFields]);

  const keyMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const f of logFields) {
      const id = (f.key ?? labelId(f.label)).toLowerCase();
      const label = f.label.toLowerCase();
      m.set(id, id);
      m.set(label, id);
    }
    return m;
  }, [logFields]);

  // Normalize filters (similar to MainPort)
  const normalizedFilters = useMemo(() => {
    return activeFilters.map((f) => {
      // If searchbar provided an op (live typing), keep it
      const token = f.op
        ? { ...f }
        : parseToken(f.key ? `${f.key}=${f.value}` : f.value);

      // Map key to canonical id
      if (token.key) {
        const k = token.key.trim().toLowerCase();
        token.key = keyMap.get(k) ?? labelId(token.key);
      }

      // If this is a STRING field and user didn't explicitly ask for exact,
      const field = token.key ? fieldById.get(token.key) : undefined;
      if (field?.type === "string") {
        const quoted = token.value.match(/^"(.*)"$/);
        if (quoted) {
          token.value = quoted[1]; // strip quotes
          token.op = "=";
        } else {
          token.op = ":"; // default to contains
        }
      }
      return token;
    });
  }, [activeFilters, keyMap, fieldById]);

  // Build predicate using advancedFilters system
  const predicate = useMemo(
    () => buildPredicate<LogItem>(normalizedFilters, logFields),
    [normalizedFilters, logFields]
  );

  // Apply predicate to filter data
  const filteredData = useMemo(
    () => tablesData.filter(predicate),
    [tablesData, predicate]
  );

    // Memoize the callback for Search Log GenericSearchbar to prevent infinite loops
    const handleSearchLogFiltersChange = useCallback((filters: ParsedFilter[]) => {
      // Separate simple search (no key) from advanced filters (with key)
      const simpleSearch = filters.find((f) => !f.key || f.key === "");
      const advancedFilters = filters.filter((f) => f.key && f.key !== "");
      
      // Update simple search value for API search
      setSimpleSearchValue(simpleSearch ? simpleSearch.value : "");
      
      // Update activeFilters with advanced filters only (for table filtering)
      setActiveFilters(advancedFilters);
    }, []);

  // Contains, like a typed `attribute: value`; the same chip twice adds nothing.
  const filterByAttribute = useCallback<FilterByAttribute>((label, value) => {
    setSearchLogChips((chips) =>
      chips.some((chip) => chip.key === label && chip.value === value)
        ? chips
        : [...chips, { key: label, value }]
    );
  }, []);

  return (
    <div className="relative min-h-[500px]">
      <div className="mb-4">
        <GenericCard isCollapsible={false}>
          <div className="p-4 flex flex-wrap items-end gap-3">
            <div className="flex flex-col">
              <label className="block mb-1 text-sm font-medium text-semantic-text-primary">
                Time range
              </label>
              <LogsTimeRangePicker
                value={range}
                mode={rangeMode}
                onChange={handleRangeChange}
                onRefresh={refreshRange}
                isLoading={isFetching}
              />
            </div>
            <GenericDropdown
              items={firstOptions}
              value={selectedFirst}
              onSelect={handleSelectFirst}
              minWidthClass="min-w-0"
              widthClass="w-full sm:w-[260px]"
              maxWidthClass="max-w-full"
              defaultLabel="-- Select instance --"
              label="CTX-R"
              size="sm"
              dropdownMaxHeightClass="max-h-48"
            />
            <GenericDropdown
              items={severityOptions}
              value={selectedSeverity}
              onSelect={setSelectedSeverity}
              minWidthClass="min-w-0"
              widthClass="w-full sm:w-[160px]"
              label="Severity Level"
              size="sm"
              dropdownMaxHeightClass="max-h-48"
            />
            {/* The page's only search: free text goes to the backend (searchTerm,
                whole range); `attribute: value` filters the loaded rows. */}
            <div className="flex flex-col flex-1 min-w-[300px]">
              <label className="block mb-1 text-sm font-medium text-semantic-text-primary">
                Search Log
              </label>
              <GenericSearchbar
                size="xs"
                placeholder="Search logs or Severity Level: ERROR"
                suggestedKeys={suggestedKeys}
                committedFilters={searchLogChips}
                onCommittedFiltersChange={setSearchLogChips}
                onFiltersChange={handleSearchLogFiltersChange}
                liveFiltering={false}
                debounceMs={200}
              />
            </div>
            {/* Not a <label>: GenericSwitch toggles on its own div click, and a
                wrapping label would re-click its hidden checkbox and undo it. */}
            <div className="flex items-center gap-2 h-8 text-sm text-semantic-text-secondary">
              <GenericSwitch checked={wrapMessages} onChange={setWrapMessages} />
              <span>Wrap messages</span>
            </div>
          </div>
        </GenericCard>
      </div>

      <div className="pb-6">
        <GenericCard
          isCollapsible={false}
          isLoading={isLoading}
          bodyContent={
            isFetching ? (
              <div className="p-4">
                <SkeletonTableRows
                  rows={5}
                  cols={5}
                  colWidths={["200px"]}
                  rowHeight={15}
                  rowGapClassName="space-y-3"
                />
              </div>
            ) : (
              <GenericTable<LogItem>
                columns={logsColumns({ wrapMessages })}
                data={filteredData}
                // The chevron is the first column (logsColumns), not GenericTable's trailing toggle.
                expandable
                showExpandColumn={false}
                renderExpanded={(row) => (
                  <LogRowDetails row={row} ctxr={selectedFirst} onFilter={filterByAttribute} />
                )}
                initialRowsPerPage={10}
                rowsPerPageOptions={[5, 10, 20, 50]}
                maxTableBodyHeight={getTableBodyMaxHeight(LOGS_CHROME_PX)}
                width="100%"
                tableClassName="bg-semantic-bg-primary"
              />
            )
          }
        />
      </div>
    </div>
  );
}

export default LogsPage;
